import type { DisabledBehavior, FlagKey } from '../config/flags'
import type { BannerSeverity } from '../config/banners'

/**
 * A string that can be either plain (same in every language) or a per-language
 * map. The app picks the value matching the current i18n language, falling
 * back to `en`, then to any defined language.
 *
 * Examples:
 *   "Plain message"
 *   { en: "Hello", da: "Hej" }
 */
export type LocalizedString = string | { [lang: string]: string }

/**
 * How the banner renders.
 *  - 'standard'         → single static row with icon, label, message, optional dismiss.
 *  - 'marquee-vertical' → rotating ticker, one message at a time, slides up
 *                         every ~4s. No dismiss. Promo-flavored, brand chrome.
 */
export type BannerDisplay = 'standard' | 'marquee-vertical'

/**
 * Optional inline link embedded inside the banner message. The token
 * `{link}` in `message` is replaced with an <a> rendering `link.text`.
 * If `message` contains no `{link}` placeholder, the link is appended
 * to the end of the message with a single space.
 */
export interface BannerLink {
  text: LocalizedString
  href: string
  /** Open in new tab — defaults to true for absolute URLs, false for site-relative. */
  external?: boolean
}

export interface RuntimeBanner {
  id: string
  severity: BannerSeverity
  message: LocalizedString
  /** Optional override for the bold severity prefix (e.g. "Note!" instead of "Info!"). */
  label?: LocalizedString
  dismissible?: boolean
  /** Comma-separated paths or '*' for all. Path matching is exact (no lang prefix). */
  pages?: string[]
  /** ISO date strings — banner only shows when current time falls in range. */
  startsAt?: string
  endsAt?: string
  /** Render mode — defaults to 'standard'. */
  display?: BannerDisplay
  /** Optional inline link rendered inside the message. */
  link?: BannerLink
}

export interface RuntimeConfig {
  flags?: Partial<Record<FlagKey, boolean>>
  disabledBehavior?: Partial<Record<FlagKey, DisabledBehavior>>
  maintenance?: {
    siteWide?: boolean
    message?: string
  }
  banners?: RuntimeBanner[]
  /** Active theme campaign id, overrides VITE_THEME. */
  theme?: string
}

/**
 * Build-time seed baked into prerendered HTML by scripts/prerender.mjs as
 * `window.__RUNTIME_CONFIG__` (a snapshot of public/config/runtime.json).
 *
 * Why this exists: prerendered pages set `isPrerendered`, which makes
 * useBootstrap start `ready=true` so the baked markup hydrates without a
 * mismatch. That skips RootLayout's `!ready` boot gate, so the first render
 * evaluated FeatureGate against an EMPTY config and fell through to
 * `defaultEnabled: true` — i.e. a page switched off in admin still rendered
 * for real until runtime.json landed and forced a re-render. Reading the seed
 * synchronously at module load means the very first render already knows the
 * flags, so a coming-soon page is coming-soon on every load, not just some.
 *
 * The prerender pass injects the SAME snapshot into the SSR global before
 * rendering, so server markup and the client's hydration render agree.
 *
 * The seed is PROVISIONAL, never authoritative. It is baked at BUILD time from
 * the repo's public/config/runtime.json, but the live file is owned by the
 * admin-api in S3 and is deliberately NOT uploaded by the deploy workflow
 * (see deploy-stg.yml — re-uploading it wiped live admin edits). So the moment
 * an admin toggles a flag, the baked seed is stale and disagrees with the live
 * config until the next build. Treating it as authoritative made a page turned
 * OFF in admin flash the real page (seed says on) before the fetch corrected
 * it — the mirror image of the bug the seed was added to fix.
 *
 * `isRuntimeConfigLoaded()` reports whether the LIVE file has landed, so
 * callers that must not act on a stale value (FeatureGate) can wait for it.
 * loadRuntimeConfig() always overwrites the seed, so admin always wins.
 */
// Declared ABOVE readSeed's call site: readSeed() runs at module evaluation and
// reaches sanitize() -> sanitizeBanner() -> VALID_SEVERITIES. `const` is not
// hoisted like a function declaration, so leaving this further down threw
// "Cannot access 'VALID_SEVERITIES' before initialization" during SSR whenever
// the seed carried a `banners` array.
const VALID_SEVERITIES = ['info', 'success', 'warning', 'critical', 'maintenance'] as const

function readSeed(): RuntimeConfig {
  const raw = (globalThis as { __RUNTIME_CONFIG__?: unknown }).__RUNTIME_CONFIG__
  return raw === undefined ? {} : sanitize(raw)
}

let current: RuntimeConfig = readSeed()
let seeded = Object.keys(current).length > 0
let loaded = false
let loadPromise: Promise<void> | null = null

export function getRuntimeConfig(): RuntimeConfig {
  return current
}

/**
 * True when the current config came from the build-time seed and the live
 * file has NOT landed yet — i.e. the values may be stale relative to admin.
 */
export function isRuntimeConfigProvisional(): boolean {
  return seeded && !loaded
}

export function isRuntimeConfigLoaded(): boolean {
  return loaded
}

export function setRuntimeConfigForTesting(config: RuntimeConfig): void {
  current = config
  loaded = true
}

export function resetRuntimeConfigForTesting(): void {
  current = readSeed()
  seeded = Object.keys(current).length > 0
  loaded = false
  loadPromise = null
}

// Exported for testability: lets tests assert dev-only logging without
// mucking with the read-only import.meta.env.DEV flag.
export function devWarn(message: string, error?: unknown, isDev = import.meta.env.DEV): void {
  if (!isDev) return
  console.warn(`[runtimeConfig] ${message}`, error ?? '')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sanitize(raw: unknown): RuntimeConfig {
  if (!isPlainObject(raw)) return {}
  const out: RuntimeConfig = {}

  if (isPlainObject(raw.flags)) {
    const flags: Partial<Record<FlagKey, boolean>> = {}
    for (const [k, v] of Object.entries(raw.flags)) {
      if (typeof v === 'boolean') flags[k as FlagKey] = v
    }
    out.flags = flags
  }

  if (isPlainObject(raw.disabledBehavior)) {
    const behavior: Partial<Record<FlagKey, DisabledBehavior>> = {}
    for (const [k, v] of Object.entries(raw.disabledBehavior)) {
      if (v === 'hide' || v === 'coming-soon' || v === 'maintenance') {
        behavior[k as FlagKey] = v
      }
    }
    out.disabledBehavior = behavior
  }

  if (isPlainObject(raw.maintenance)) {
    out.maintenance = {
      siteWide: typeof raw.maintenance.siteWide === 'boolean' ? raw.maintenance.siteWide : undefined,
      message: typeof raw.maintenance.message === 'string' ? raw.maintenance.message : undefined,
    }
  }

  if (Array.isArray(raw.banners)) {
    out.banners = raw.banners
      .map(sanitizeBanner)
      .filter((b): b is RuntimeBanner => b !== null)
  }

  if (typeof raw.theme === 'string') out.theme = raw.theme

  return out
}

/**
 * Validates a LocalizedString — either a non-empty string, or an object
 * with at least one string value. Non-string values are dropped. Returns
 * the cleaned value or null if nothing usable remains.
 */
export function sanitizeLocalizedString(raw: unknown): LocalizedString | null {
  if (typeof raw === 'string') return raw.length > 0 ? raw : null
  if (!isPlainObject(raw)) return null
  const out: { [lang: string]: string } = {}
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'string' && v.length > 0) out[k] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

function sanitizeBanner(raw: unknown): RuntimeBanner | null {
  if (!isPlainObject(raw)) return null
  if (typeof raw.id !== 'string' || raw.id.length === 0) return null
  if (typeof raw.severity !== 'string' || !VALID_SEVERITIES.includes(raw.severity as BannerSeverity)) {
    return null
  }
  const message = sanitizeLocalizedString(raw.message)
  if (!message) return null

  const banner: RuntimeBanner = {
    id: raw.id,
    severity: raw.severity as BannerSeverity,
    message,
  }

  const label = sanitizeLocalizedString(raw.label)
  if (label) banner.label = label

  if (typeof raw.dismissible === 'boolean') banner.dismissible = raw.dismissible
  if (Array.isArray(raw.pages)) {
    banner.pages = raw.pages.filter((p): p is string => typeof p === 'string' && p.length > 0)
  }
  if (typeof raw.startsAt === 'string') banner.startsAt = raw.startsAt
  if (typeof raw.endsAt === 'string') banner.endsAt = raw.endsAt
  if (raw.display === 'marquee-vertical' || raw.display === 'standard') {
    banner.display = raw.display
  }
  const link = sanitizeBannerLink(raw.link)
  if (link) banner.link = link

  return banner
}

function sanitizeBannerLink(raw: unknown): BannerLink | null {
  if (!isPlainObject(raw)) return null
  if (typeof raw.href !== 'string' || raw.href.length === 0) return null
  const text = sanitizeLocalizedString(raw.text)
  if (!text) return null
  const link: BannerLink = { text, href: raw.href }
  if (typeof raw.external === 'boolean') link.external = raw.external
  return link
}

/**
 * Loads runtime config once. Subsequent calls return the cached promise.
 * Failure is silent for users; logged in dev only (Q33).
 */
export async function loadRuntimeConfig(): Promise<void> {
  if (loadPromise) return loadPromise
  if (parseBool(import.meta.env.VITE_RUNTIME_CONFIG_ENABLED as string | undefined) === false) {
    loaded = true
    return Promise.resolve()
  }

  const urlEnv = (import.meta.env.VITE_RUNTIME_CONFIG_URL as string | undefined)?.trim()
  const url = urlEnv && urlEnv.length > 0 ? urlEnv : '/config/runtime.json'

  loadPromise = (async () => {
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) {
        devWarn(`fetch failed with status ${res.status} for ${url}`)
        return
      }
      const json = (await res.json()) as unknown
      current = sanitize(json)
    } catch (err) {
      devWarn(`fetch threw for ${url}`, err)
    } finally {
      loaded = true
    }
  })()

  return loadPromise
}

function parseBool(raw: string | undefined): boolean | undefined {
  /* v8 ignore start */
  if (raw === undefined) return undefined
  /* v8 ignore stop */
  const lowered = raw.trim().toLowerCase()
  if (lowered === 'true' || lowered === '1') return true
  if (lowered === 'false' || lowered === '0') return false
  return undefined
}
